/*
 * Generic add-to-bag handler. Two entry points:
 *   1. Click delegation on any element with [data-add-to-bag] that
 *      carries [data-variant-id]. Use for declarative buttons anywhere
 *      in the theme (future wishlist quick-add, recommendations, bundle
 *      blocks, etc.). Skipped when the element has aria-disabled="true".
 *   2. Custom event `product-card:add` (dispatched by product-card.js
 *      when a size pill is clicked in the quick-add drawer).
 *
 * Both paths POST to /cart/add.js (mirroring the PDP flow in
 * assets/main-product.js) and dispatch `cart:added` on success / `cart:error`
 * on failure so mini-bag.js — and any other consumer — can refresh, open
 * the drawer, or render an error.
 *
 * Pending consolidation into the D16 cart-client utility (TECH-DEBT #7);
 * for now PDP and mini-bag upsell keep their own POST. Promote to that
 * utility when the 4th caller appears (e.g. wishlist add-to-bag).
 */
(function () {
  'use strict';

  // The .js endpoint returns the added line item as JSON (the bare cart_add_url returns the HTML cart
  // page), so the notification can render the price from it without a second /cart.js round-trip.
  const ADD_URL = (function () {
    const url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    return url.endsWith('.js') ? url : url + '.js';
  })();
  const TIMEOUT_MS = 8000;
  const LOADING_ATTR = 'data-add-to-bag-loading';

  function setBusy(trigger, busy) {
    if (!trigger) return;
    if (busy) {
      trigger.setAttribute(LOADING_ATTR, '');
      if (trigger.hasAttribute('data-add-to-bag')) {
        trigger.setAttribute('aria-busy', 'true');
        trigger.setAttribute('aria-disabled', 'true');
      }
    } else {
      trigger.removeAttribute(LOADING_ATTR);
      if (trigger.hasAttribute('data-add-to-bag')) {
        trigger.removeAttribute('aria-busy');
        trigger.removeAttribute('aria-disabled');
      }
    }
  }

  // The card (or its container) the shopper clicked already shows the sale compare-at price, so read it
  // for the notification to render instantly without waiting on a /products fetch. Covers the regular
  // product card (.line-through) and the Voyado card ([data-slot="compare"]); null when none is shown.
  function displayedCompare(trigger) {
    if (!trigger || !trigger.closest) return null;
    var card = trigger.matches('[data-voyado-card], [data-product-card]')
      ? trigger
      : trigger.closest('[data-voyado-card], [data-product-card]');
    if (!card) return null;
    var el = card.querySelector('[data-slot="compare"], .line-through');
    // innerHTML (not textContent) so the AED dirham glyph markup carries through to the notification.
    var txt = el && el.innerHTML.trim();
    return txt || null;
  }

  async function addToBag(variantId, trigger) {
    setBusy(trigger, true);
    const controller = new AbortController();
    const timeout = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    try {
      const res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('add_failed');
      // /cart/add.js already returns the added line item (image, title, prices, handle). Pass it on so
      // the notification can render from it immediately instead of making a second /cart.js round-trip.
      var addedItem = null;
      try { addedItem = await res.json(); } catch (e) {}
      document.dispatchEvent(new CustomEvent('cart:added', {
        bubbles: true,
        detail: { trigger: trigger || null, variantId: String(variantId), item: addedItem, compareText: displayedCompare(trigger) },
      }));
    } catch (err) {
      document.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          reason: err && err.name === 'AbortError' ? 'timeout' : 'add_failed',
          trigger: trigger || null,
        },
      }));
    } finally {
      clearTimeout(timeout);
      setBusy(trigger, false);
    }
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-add-to-bag]');
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    e.preventDefault();
    addToBag(variantId, btn);
  });

  document.addEventListener('product-card:add', function (e) {
    const detail = e.detail || {};
    if (!detail.variantId) return;
    addToBag(detail.variantId, detail.card || null);
  });
})();
